import Breadcrumbs from "@/components/Breadcrumbs";
import { SettingsService } from "@/services/admin-services";
import { LocaleService } from "@/services/global-services";
import { Language, Locale, UseCase } from "@/services/global-services/models";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { chain } from "lodash";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const breadcrumbs = [
    {
        to: "../..",
        title: "Administration",
    },
    {
        title: "Settings",
        to: "../..",
    },
    {
        title: "Use-cases",
        to: "",
    },
];

type LanguageSpecificUseCases = UseCase & { language: Language };

const Overview = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [data, setData] = useState<Array<LanguageSpecificUseCases>>([]);
    const [languages, setLanguages] = useState<Array<Language>>([]);

    const settingsService = useMemo(() => new SettingsService("int"), []);

    const navigate = useNavigate();

    useEffect(() => {
        LocaleService.get().then(parseLanguages);
    }, [settingsService]);

    const parseLanguages = (regions: Array<Locale>) => {
        const languages: Array<Language> = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({
                    ...language,
                    slug: `int-${split[1]}`,
                    regionLabel: "International",
                });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    useEffect(() => {
        if (languages.length === 0) return;

        const useCasesPromises = [];
        for (let i = 0; i < languages.length; i++) {
            const lang = languages[i];
            useCasesPromises.push(
                settingsService.getUseCases(lang.slug).then((useCases) => {
                    return useCases.map((useCase) => ({ ...useCase, language: lang }));
                })
            );
        }

        Promise.all(useCasesPromises).then((results) => {
            setData(results.flat());
            setIsLoading(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [languages]);

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbs} />

            <Divider />

            <Grid container spacing={2} p={{ xs: 2, md: 4 }}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table">
                            <TableHead sx={{ bgcolor: "alternate.main" }}>
                                <TableRow>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Code
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Language
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Use-case
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 100 }}>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Link
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 200 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={3}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    chain(data)
                                        .groupBy((x) => x.code)
                                        .sortBy(data, [(d) => d[0].code])
                                        .value()
                                        .map((useCaseGroup, key) => {
                                            const firstItem = useCaseGroup[0];

                                            return (
                                                <Fragment key={key}>
                                                    <TableRow>
                                                        <TableCell rowSpan={useCaseGroup.length}>
                                                            <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                                {firstItem.code}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                                {firstItem.language.label}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                                {firstItem.name}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                                {firstItem.internalLink ? (
                                                                    <CheckIcon className="text-green-600 w-5 h-5" />
                                                                ) : (
                                                                    <XMarkIcon className="text-red-600 w-5 h-5" />
                                                                )}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell scope="row" sx={{ p: 0 }}>
                                                            <Box sx={{ display: "flex" }}>
                                                                <Button
                                                                    color={"primary"}
                                                                    sx={{ ml: "auto", mr: 1 }}
                                                                    onClick={() =>
                                                                        navigate(
                                                                            `/admin/settings/use-cases/${firstItem.language.slug}/${firstItem.id}`
                                                                        )
                                                                    }
                                                                >
                                                                    Manage link
                                                                </Button>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                    {useCaseGroup.slice(1).map((useCase, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>
                                                                <Typography
                                                                    color={"text.secondary"}
                                                                    variant={"subtitle2"}
                                                                >
                                                                    {useCase.language.label}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography
                                                                    color={"text.secondary"}
                                                                    variant={"subtitle2"}
                                                                >
                                                                    {useCase.name}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography
                                                                    color={"text.secondary"}
                                                                    variant={"subtitle2"}
                                                                >
                                                                    {useCase.internalLink ? (
                                                                        <CheckIcon className="text-green-600 w-5 h-5" />
                                                                    ) : (
                                                                        <XMarkIcon className="text-red-600 w-5 h-5" />
                                                                    )}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell scope="row" sx={{ p: 0 }}>
                                                                <Box sx={{ display: "flex" }}>
                                                                    <Button
                                                                        color={"primary"}
                                                                        sx={{ ml: "auto", mr: 1 }}
                                                                        onClick={() =>
                                                                            navigate(
                                                                                `/admin/settings/use-cases/${useCase.language.slug}/${useCase.id}`
                                                                            )
                                                                        }
                                                                    >
                                                                        Manage link
                                                                    </Button>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </Fragment>
                                            );
                                        })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Overview;
